import "./tabs.scss";

import * as RadixTabs from "@radix-ui/react-tabs";
import cn from "clsx";
import { Alignment, EditAttributes } from "design-system/types/types";
import { ReactNode, useEffect, useRef } from "react";

export interface TabProps {
  tabLabel: string;
  tabContent: ReactNode;
  tabContentPadding?: boolean;

  editAttributes?: {
    tabLabel?: EditAttributes;
  };
}

export interface TabsProps {
  tabs: Array<TabProps>;
  ariaLabel?: string;
  align?: Alignment;
}

export const getTabHash = (label?: string) =>
  label ? label.replace(/[^A-Za-z0-9]/g, "") : "";

function onTabValueChange(value: string) {
  window.history.pushState({}, "", `#${value}`);

  const event = new CustomEvent("hashchange", {
    detail: value || "",
  });
  dispatchEvent(event);
}

/**
 * ## Implementation Notes
 * - This component utilizes the [Radix Tabs](https://www.radix-ui.com/docs/primitives/components/tabs) component
 *
 * ## See it in use on...
 * - The [event/conference page](/story/example-pages-secondary-page-types--story)
 *
 * - **`id: P-024-000-00`**
 */
export function Tabs({ tabs, ariaLabel, align }: TabsProps) {
  const wrapperClass = cn(align && `hbs-global-align-${align}`);
  const tabRefs = useRef<Array<HTMLButtonElement>>([]);
  const prevHash = useRef<string>();

  const focusHashedTab = () => {
    const hash = window.location.hash.replace("#", "");
    const focusedTab = tabRefs.current.find((ref) => ref.dataset.hash === hash);
    if (focusedTab) {
      focusedTab.focus();
      const activeTab = tabRefs.current.find(
        (ref) => ref.getAttribute("aria-selected") === "true",
      );
      const event = new CustomEvent("hashchange", {
        detail: focusedTab || activeTab?.dataset.hash,
      });
      if (!activeTab || prevHash.current !== activeTab?.dataset.hash) {
        prevHash.current = activeTab?.dataset.hash || "";
        dispatchEvent(event);
      }
    }
  };

  useEffect(() => {
    focusHashedTab();
    window.addEventListener("hashchange", focusHashedTab);
    return () => window.removeEventListener("hashchange", focusHashedTab);
  }, []);

  return (
    <div className={wrapperClass} data-region="p__tabs">
      <div className="hbs-tabs">
        <RadixTabs.Root
          className="hbs-tabs__wrapper"
          defaultValue={getTabHash(tabs[0]?.tabLabel)}
          onValueChange={onTabValueChange}
        >
          <RadixTabs.List
            className="hbs-tabs__trigger-list"
            aria-label={ariaLabel || "Tabs"}
          >
            {tabs.map((tab, i) => {
              const value = getTabHash(tab?.tabLabel);
              return (
                <RadixTabs.Trigger
                  {...tab.editAttributes?.tabLabel}
                  key={i + value}
                  value={value || ""}
                  data-hash={value}
                  className="hbs-tabs__tab-trigger"
                  ref={(ref) => {
                    ref && tabRefs.current.push(ref);
                  }}
                >
                  <h2 className="hbs-tabs__tab-header">{tab.tabLabel}</h2>
                </RadixTabs.Trigger>
              );
            })}
          </RadixTabs.List>

          {tabs.map((tab, i) => (
            <RadixTabs.Content
              key={i}
              value={getTabHash(tab?.tabLabel)}
              className={cn(
                "hbs-tabs__tab-content",
                tab.tabContentPadding && "hbs-tabs__tab-content--padding",
              )}
            >
              {tab.tabContent}
            </RadixTabs.Content>
          ))}
        </RadixTabs.Root>
      </div>
    </div>
  );
}
